import { UserRoundX } from '@/lib/icons';
import { Badge } from 'prosci-ui';
import { ReportPageLayout } from '@/components/reports';
import { reportsService } from '@/services/reports-service';
import type { ColumnDef, FilterConfig, ReportFilters } from '@/types/reports';

interface ElearningStatus {
  id: string;
  attributes: {
    user_info: {
      first_name: string;
      last_name: string;
      email: string;
    };
    product_name: string;
    is_complete: boolean;
    date_started?: string;
    last_access_date?: string;
    affiliate_name?: string;
  };
}

const columns: ColumnDef<ElearningStatus>[] = [
  {
    key: 'user',
    header: 'User',
    render: (item) => (
      <div>
        <div className="font-medium">
          {item.attributes.user_info.first_name} {item.attributes.user_info.last_name}
        </div>
        <div className="text-muted-foreground text-xs">
          {item.attributes.user_info.email}
        </div>
        {item.attributes.affiliate_name && (
          <div className="text-muted-foreground text-xs mt-0.5">
            {item.attributes.affiliate_name}
          </div>
        )}
      </div>
    ),
  },
  {
    key: 'product_name',
    header: 'Course',
    render: (item) => item.attributes.product_name,
  },
  {
    key: 'is_complete',
    header: 'Progress',
    render: (item) =>
      item.attributes.is_complete ? (
        <Badge variant="success" tint>
          Complete
        </Badge>
      ) : (
        <Badge variant="warning" tint>
          In Progress
        </Badge>
      ),
  },
  {
    key: 'dates',
    header: 'Dates',
    render: (item) => (
      <div className="text-sm">
        <div className="text-xs">
          <span className="font-medium">Started:</span>{' '}
          {item.attributes.date_started
            ? new Date(item.attributes.date_started).toLocaleDateString()
            : '-'}
        </div>
        <div className="text-muted-foreground text-xs mt-0.5">
          <span className="font-medium">Last:</span>{' '}
          {item.attributes.last_access_date
            ? new Date(item.attributes.last_access_date).toLocaleDateString()
            : '-'}
        </div>
      </div>
    ),
  },
];

const filterConfig: FilterConfig = {
  fields: [
    { key: 'product_id', type: 'elearning-product-select', label: 'eLearning Course', placeholder: 'All eLearning Courses' },
    { key: 'email_domain', type: 'text', label: 'Email Domain', placeholder: 'e.g., prosci.com' },
    { key: 'from_date', type: 'date', label: 'From Date', placeholder: 'Select date' },
    { key: 'to_date', type: 'date', label: 'To Date', placeholder: 'Select date' },
    { key: 'affiliate_id', type: 'affiliate-select', label: 'Affiliate', placeholder: 'All Affiliates', showFor: ['super', 'admin', 'affiliate_admin'] },
    { key: 'license_admin_id', type: 'organization-select', label: 'License Holder', placeholder: 'All License Holders', showFor: ['super', 'admin', 'affiliate_admin'] },
  ],
  hasFiltersCheck: (filters: ReportFilters) =>
    Boolean(
      filters.email_domain ||
      filters.from_date ||
      filters.to_date ||
      filters.affiliate_id ||
      filters.license_admin_id ||
      filters.product_id
    ),
};

export function ElearningTrackingReport() {
  return (
    <ReportPageLayout<ElearningStatus>
      title="eLearning Tracking Report"
      description="Track eLearning course enrollments and completion status."
      breadcrumbLabel="eLearning Tracking"
      itemLabel="enrollments"
      queryKey="elearning-tracking"
      fetchData={reportsService.getElearningTracking}
      downloadData={reportsService.downloadElearningTracking}
      filterConfig={filterConfig}
      columns={columns}
      sortData={(data) =>
        [...data].sort((a, b) => {
          const dateA = a.attributes.last_access_date
            ? new Date(a.attributes.last_access_date).getTime()
            : 0;
          const dateB = b.attributes.last_access_date
            ? new Date(b.attributes.last_access_date).getTime()
            : 0;
          return dateB - dateA;
        })
      }
      getRowKey={(item) => item.id}
      emptyIcon={<UserRoundX />}
      emptyTitle="No eLearning enrollments found"
      emptyDescription="Try adjusting your filters to find enrollments."
    />
  );
}
